<template>
  <div class="signup mb-5">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h1 class="text-center text-xl bold my-5 text-uppercase">{{$t('auth.signup')}}</h1>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-5">
          <b-alert v-if="formError" show variant="danger">
            <span class="text-sm">{{formError}}</span>
          </b-alert>
          <div class="form">
            <ValidationObserver ref="observer" v-slot="{ invalid }">
              <b-form @submit="onSubmit" @submit.stop.prevent>
                <div class="mb-3">
                  <validation-provider rules="required|max:50" v-slot="{ errors }">
                    <b-input-group id="input-group-username">
                      <b-input-group-prepend is-text>
                        <b-icon icon="person-fill"></b-icon>
                      </b-input-group-prepend>
                      <b-form-input id="input-username"
                                    class="text-sm"
                                    v-model="form.username"
                                    type="text"
                                    :placeholder="$t('auth.userName')"
                                    :disabled="formSending"
                                    required
                      ></b-form-input>
                    </b-input-group>
                    <span class="form-error text-sm">{{ errors[0] }}</span>
                  </validation-provider>
                </div>
                <div class="mb-3">
                  <validation-provider rules="required|email|max:50" v-slot="{ errors }">
                    <b-input-group id="input-group-email">
                      <b-input-group-prepend is-text>
                        <b-icon icon="at"></b-icon>
                      </b-input-group-prepend>
                      <b-form-input id="input-email"
                                    class="text-sm"
                                    v-model="form.email"
                                    type="email"
                                    :placeholder="$t('forms.emailAddress')"
                                    :disabled="formSending"
                                    required
                      ></b-form-input>
                    </b-input-group>
                    <span class="form-error text-sm">{{ errors[0] }}</span>
                  </validation-provider>
                </div>
                <div class="mb-3">
                  <b-input-group id="input-group-name">
                    <b-input-group-prepend is-text>
                      <b-icon icon="person-fill"></b-icon>
                    </b-input-group-prepend>
                    <b-form-input id="input-firstName"
                                  class="text-sm"
                                  v-model="form.firstName"
                                  type="text"
                                  :placeholder="$t('auth.firstName')"
                                  :disabled="formSending"
                    ></b-form-input>
                    <b-form-input id="input-lastName"
                                  class="text-sm"
                                  v-model="form.lastName"
                                  type="text"
                                  :placeholder="$t('auth.lastName')"
                                  :disabled="formSending"
                    ></b-form-input>
                  </b-input-group>
                </div>
                <div class="mb-3">
                  <validation-provider rules="required|min:8|max:30" v-slot="{ errors }">
                    <b-input-group id="input-group-password">
                      <b-input-group-prepend is-text>
                        <b-icon icon="key"></b-icon>
                      </b-input-group-prepend>
                      <b-form-input id="input-password"
                                    class="text-sm"
                                    v-model="form.password"
                                    :type="!isPasswordVisible ? 'password' : 'text'"
                                    :placeholder="$t('auth.password')"
                                    :disabled="formSending"
                                    required
                      ></b-form-input>
                      <b-input-group-append is-text @click="tooglePasswordVisibility" class="pointer">
                        <b-icon v-if="!isPasswordVisible" icon="eye"></b-icon>
                        <b-icon v-if="isPasswordVisible" icon="eye-slash"></b-icon>
                      </b-input-group-append>
                    </b-input-group>
                    <span class="form-error text-sm">{{ errors[0] }}</span>
                  </validation-provider>
                </div>
                <div class="mb-3">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <b-form-select v-model="form.userType"
                                   :options="userTypes"
                                   class="text-sm"
                                   :disabled="formSending"></b-form-select>
                    <span class="form-error text-sm">{{ errors[0] }}</span>
                  </validation-provider>
                </div>
                <div class="mb-5">
                  <validation-provider :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                    <b-form-checkbox id="input-checkbox"
                                     v-model="form.consent"
                                     :disabled="formSending"
                                     class="text-sm">
                      <i18n path="auth.accept" tag="span">
                        <a :href="privacyPolicyUrl" target="_blank">
                        <span class="text-underline">{{ $t('auth.privacyPolicy') }}</span>
                        </a>
                      </i18n>
                    </b-form-checkbox>
                    <span class="form-error text-sm">{{ errors[0] }}</span>
                  </validation-provider>
                </div>
                <div class="d-flex justify-content-center">
                  <b-button type="submit"
                            :disabled="invalid || formSending"
                            variant="primary"
                            class="btn-rounded px-5 py-2 d-flex justify-content-center align-items-center">
                    <span class="text-md text-uppercase">{{$t('auth.register')}}</span>
                    <b-spinner v-if="formSending" type="grow" small class="ml-3"></b-spinner>
                  </b-button>
                </div>
              </b-form>
            </ValidationObserver>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import {
  required, email, min, max,
} from 'vee-validate/dist/rules';
import { i18n } from 'vue-lang-router';
import { PRIVACY_POLICY_URL } from '../config';

extend('required', {
  ...required,
  message: i18n.t('forms.thisFieldIsRequired'),
});
extend('email', {
  ...email,
  message: i18n.t('forms.emailNotValid'),
});
extend('min', {
  ...min,
  message: i18n.t('forms.atLeastCharacters'),
});
extend('max', {
  ...max,
  message: i18n.t('forms.maxCharacters'),
});

export default {
  metaInfo() {
    return {
      title: `${this.$t('auth.signup')} - Nissens Support`,
    };
  },
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      form: {
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        userType: null,
        consent: false,
      },
      privacyPolicyUrl: PRIVACY_POLICY_URL,
      userTypes: [
        {
          value: null,
          text: `-- ${this.$t('auth.selectUserType')} --`,
        },
        {
          value: 'mechanic',
          text: this.$t('auth.mechanic'),
        },
        {
          value: 'wholesaler',
          text: this.$t('auth.wholesaler'),
        },
        {
          value: 'car_owner',
          text: this.$t('auth.carOwner'),
        },
      ],
      isPasswordVisible: false,
      formSending: false,
      formError: '',
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.user.status.loggedIn;
    },
  },
  mounted() {
    if (this.loggedIn) {
      this.$router.push('/profile');
    }
  },
  methods: {
    onSubmit() {
      this.$refs.observer.validate().then(() => {
        this.formSending = true;
        this.formError = '';
        this.$store.dispatch('user/REGISTER', {
          username: this.form.username,
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          password: this.form.password,
          userType: this.form.userType,
          locale: this.$i18n.locale,
        }).then(() => {
          this.$root.$bvToast.toast('Success', {
            title: this.$t('auth.registerSuccess'),
            variant: 'light',
            solid: true,
            autoHideDelay: 7000,
            toaster: 'b-toaster-bottom-center',
            bodyClass: 'd-none',
            toastClass: 'toast-secondary',
          });
          this.$router.push('/login');
        }).catch((error) => {
          this.formError = error;
          this.formSending = false;
        });
      });
    },
    tooglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.signup {
  form ::v-deep {
    .input-group-prepend > div {
      background-color: white;
    }
    #input-group-name {
      > span {
        display: block;
      }
    }
    #input-group-password {
      input {
        border-right: 0;
      }
      .input-group-append .input-group-text {
        background-color: transparent;
        border-left: 0;
      }
    }
    input, select {
      padding: .8rem 1rem;
      height: unset;
    }
    svg {
      color: #c7c7c7;
    }
  }
  .form-error {
    color: $tertiary;
  }
}
</style>
